import ks from "@shared/kalmstromShared4";
import { IListDefinition, IFieldDefinition } from "./DBInterfaces";
import { CONFIG } from "./DBConstants";
import DBState from "./DBState";
import DBModalManager from "./DBModalManager";
import DBStringUtils from "./DBStringUtils";
import DBFieldManager from "./DBFieldManager";
import DBCanvas from "./DBCanvas";
import DBDragDrop from "./DBDragDrop";
import DBViewManager from "./DBViewManager";

export default class DBListManager {

    public static AddList = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBModalManager.OpenModal("addListModal");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.AddList");
        }
    };

    public static CreateListOrLibrary = (type: string, libraryType: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("canvasPanel");
            const containerWidth: number = container !== null ? container.clientWidth : 1200;
            const listCount: number = DBState.Lists.length;

            const x: number = 40 + (listCount * 40) % (containerWidth - 460);
            const y: number = 40 + (listCount * 40);

            const titleAndTemplate: { title: string; template: number } = DBListManager.GetNewListInfo(type, libraryType);

            const list: IListDefinition = {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: titleAndTemplate.title,
                Properties: {},
                Data: [],
                Views: [],
                Position: { X: x, Y: y },
                Collapsed: false,
                ListType: type,
                ListTemplate: titleAndTemplate.template,
                LibraryType: libraryType,
                UseContentType: false,
                ContentTypeName: "",
                ContentTypeGroup: "Custom Content Types",
                Settings: null
            };

            DBState.Lists.push(list);
            DBState.SaveState();
            DBListManager.RenderLists();
            DBModalManager.CloseModal("addListModal");
            DBModalManager.ShowToast((libraryType || "List") + " added - drag to reposition", "success");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.CreateListOrLibrary");
        }
    };

    private static GetNewListInfo = (type: string, libraryType: string): { title: string; template: number } => {
        if (ks.kErrorMode === true) { return { title: "", template: 0 }; }
        try {
            if (type === "library") {
                const title: string = libraryType.replace(" Library", "");
                let template: number = 100;
                if (libraryType === "Document Library") { template = 101; }
                else if (libraryType === "Picture Library") { template = 109; }
                return { title: title, template: template };
            }
            return { title: "List" + (DBState.Lists.length + 1), template: 100 };
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.GetNewListInfo");
            return { title: "", template: 0 };
        }
    };

    public static UpdateListName = (listId: string, newName: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list !== null && newName.trim()) {
                list.Title = newName.trim();
                DBState.SaveState();
                DBListManager.RenderLists();
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.UpdateListName");
        }
    };

    public static DeleteList = (listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }

            const fieldCount: number = Object.keys(list.Properties || {}).length;
            const dataCount: number = list.Data !== null && list.Data !== undefined ? list.Data.length : 0;

            DBModalManager.ShowConfirmation({
                title: "Delete List",
                message: 'Are you sure you want to delete the list "' + list.Title + '"?',
                details: "This will permanently delete " + fieldCount + " field(s) and " + dataCount + " data row(s).",
                confirmText: list.Title,
                confirmLabel: "Delete",
                onConfirm: DBListManager.CreateDeleteHandler(listId)
            });
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.DeleteList");
        }
    };

    private static CreateDeleteHandler = (listId: string): (() => void) => {
        const NoOp = (): void => { return; };
        if (ks.kErrorMode === true) { return NoOp; }
        try {
            const HandleDelete = (): void => {
                const FilterById = (l: IListDefinition): boolean => { return l.Id !== listId; };
                DBState.Lists = DBState.Lists.filter(FilterById);
                DBState.SaveState();
                DBListManager.RenderLists();
                DBModalManager.ShowToast("List deleted", "success");
            };
            return HandleDelete;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.CreateDeleteHandler");
            return NoOp;
        }
    };

    public static DuplicateList = (listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }

            const posX: number = (list.Position !== null && list.Position !== undefined ? list.Position.X : 40) + 40;
            const posY: number = (list.Position !== null && list.Position !== undefined ? list.Position.Y : 40) + 40;

            const newList: IListDefinition = {
                Id: "list_" + (++DBState.FieldIdCounter),
                Title: list.Title + " (Copy)",
                Properties: JSON.parse(JSON.stringify(list.Properties)),
                Settings: list.Settings !== null ? JSON.parse(JSON.stringify(list.Settings)) : null,
                Data: list.Data !== null ? JSON.parse(JSON.stringify(list.Data)) : [],
                Views: list.Views !== null ? JSON.parse(JSON.stringify(list.Views)) : [],
                Position: { X: posX, Y: posY },
                Collapsed: false,
                ListType: list.ListType,
                ListTemplate: list.ListTemplate,
                LibraryType: list.LibraryType,
                UseContentType: list.UseContentType || false,
                ContentTypeName: list.ContentTypeName || "",
                ContentTypeGroup: list.ContentTypeGroup || "Custom Content Types"
            };
            DBState.Lists.push(newList);
            DBState.SaveState();
            DBListManager.RenderLists();
            DBModalManager.ShowToast("List duplicated", "success");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.DuplicateList");
        }
    };

    public static RenderLists = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const emptyState: HTMLElement | null = document.getElementById("emptyState");
            const container: HTMLElement | null = document.getElementById("listsContainer");
            if (container === null) { return; }

            if (DBState.Lists.length === 0) {
                if (emptyState !== null) { emptyState.style.display = "block"; }
                container.innerHTML = "";
                DBCanvas.ClearRelationships();
                return;
            }

            if (emptyState !== null) { emptyState.style.display = "none"; }
            container.innerHTML = "";

            const RenderOne = (list: IListDefinition): void => {
                DBListManager.RenderSingleCard(container, list);
            };
            DBState.Lists.forEach(RenderOne);

            DBCanvas.UpdateCanvasSize();
            const DrawAfterLayout = (): void => {
                const DrawNow = (): void => { DBCanvas.DrawRelationships(); };
                requestAnimationFrame(DrawNow);
            };
            requestAnimationFrame(DrawAfterLayout);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.RenderLists");
        }
    };

    public static RenderSingleCard = (container: HTMLElement, list: IListDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const card: HTMLDivElement = document.createElement("div");
            card.className = "list-card";
            if (list.Collapsed) { card.classList.add("collapsed"); }
            card.id = "card_" + list.Id;
            card.style.left = (list.Position !== null && list.Position !== undefined ? list.Position.X : 40) + "px";
            card.style.top = (list.Position !== null && list.Position !== undefined ? list.Position.Y : 40) + "px";

            const isSelected: boolean = DBState.SelectedListId === list.Id;
            if (isSelected) { card.classList.add("selected"); }

            card.innerHTML = DBListManager.BuildCardHtml(list);
            container.appendChild(card);

            DBCanvas.SetupCardDragging(card, list.Id);
            DBListManager.SetupTitleEditing(card, list.Id);
            DBDragDrop.SetupDropZone(card.querySelector("#dropzone_" + list.Id), list.Id);
            DBDragDrop.SetupDropZone(card, list.Id);
            DBFieldManager.RenderFields(list);
            DBViewManager.RenderViewsOnCard(list);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.RenderSingleCard");
        }
    };

    private static BuildCardHtml = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const dataCount: number = list.Data !== null && list.Data !== undefined ? list.Data.length : 0;
            const dataBadgeClass: string = dataCount > 0 ? "data-badge has-data" : "data-badge";
            const viewCount: number = list.Views !== null && list.Views !== undefined ? list.Views.length : 0;
            const hasViewsClass: string = viewCount > 0 ? "has-views" : "";
            const ctActiveClass: string = list.UseContentType ? "ct-active" : "";
            const eid: string = DBStringUtils.EscapeHtml(list.Id);
            const etitle: string = DBStringUtils.EscapeHtml(list.Title);
            const ctName: string = DBStringUtils.EscapeHtml(DBStringUtils.GetContentTypeName(list));
            const settingsStyle: string = DBListManager.GetSettingsStyle(list);
            const libraryBadge: string = DBListManager.GetLibraryBadge(list);
            const ctIndicator: string = DBListManager.GetCtIndicator(list, ctName);

            return DBListManager.BuildToolbarHtml(eid, dataBadgeClass, dataCount, hasViewsClass, viewCount, ctActiveClass, ctName, settingsStyle)
                + DBListManager.BuildTitleAndBodyHtml(eid, etitle, libraryBadge, ctIndicator, list.Id);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.BuildCardHtml");
            return "";
        }
    };

    private static BuildToolbarHtml = (eid: string, dataBadgeClass: string, dataCount: number, hasViewsClass: string, viewCount: number, ctActiveClass: string, ctName: string, settingsStyle: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const ctTitle: string = ctActiveClass ? "Content Type: " + ctName + " (click to disable)" : "Enable Content Type mode (site columns + CT)";
            return '<div class="list-header"><div class="list-toolbar">'
                + '<button class="toolbar-action-btn data-badge-btn ' + dataBadgeClass + '" data-action="open-data-modal" data-list-id="' + eid + '" title="Manage data (' + dataCount + ' rows)"><span>\u{1F4CA}</span> ' + dataCount + '</button>'
                + '<button class="toolbar-action-btn views-badge-btn ' + hasViewsClass + '" data-action="manage-views" data-list-id="' + eid + '" title="Manage views (' + viewCount + ' views)"><span>\u{1F441}</span> ' + viewCount + '</button>'
                + '<button class="toolbar-action-btn ct-toggle-btn ' + ctActiveClass + '" data-action="toggle-content-type" data-list-id="' + eid + '" title="' + ctTitle + '"><span>\u{1F3F7}</span></button>'
                + '<button class="toolbar-action-btn" data-action="open-field-editor" data-list-id="' + eid + '" title="Edit Fields"><span>\u270F</span></button>'
                + '<div style="flex: 1;"></div>'
                + '<button class="toolbar-action-btn" data-action="open-list-settings" data-list-id="' + eid + '" title="List Settings" ' + settingsStyle + '><span>\u2699</span></button>'
                + '<button class="toolbar-action-btn" data-action="toggle-collapse" data-list-id="' + eid + '" title="Collapse/Expand"><span>\u2195</span></button>'
                + '<button class="toolbar-action-btn" data-action="duplicate-list" data-list-id="' + eid + '" title="Duplicate"><span>\u2398</span></button>'
                + '<button class="toolbar-action-btn" data-action="delete-list" data-list-id="' + eid + '" title="Delete"><span>\u00D7</span></button>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.BuildToolbarHtml");
            return "";
        }
    };

    private static BuildTitleAndBodyHtml = (eid: string, etitle: string, libraryBadge: string, ctIndicator: string, listId: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            return '<div class="list-title-row"><div class="list-header-title">'
                + '<div class="list-name-display" title="' + etitle + '">' + etitle + '</div>'
                + libraryBadge + ctIndicator
                + '</div></div></div>'
                + '<div class="list-body">'
                + '<div class="auto-fields-note">Auto: <span>ID</span>, <span>Title</span></div>'
                + '<div id="fieldList_' + listId + '"></div>'
                + '<div class="drop-zone" id="dropzone_' + listId + '">'
                + '<div style="font-size: 20px; margin-bottom: 6px;">\u2B07\uFE0F</div>'
                + '<div>Drop column here</div></div></div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.BuildTitleAndBodyHtml");
            return "";
        }
    };

    private static GetSettingsStyle = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (list.Settings !== null && list.Settings !== undefined) {
                const keys: string[] = Object.keys(list.Settings);
                if (keys.length > 0) {
                    return 'style="background: #dbeafe; border-color: #3b82f6;"';
                }
            }
            return "";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.GetSettingsStyle");
            return "";
        }
    };

    private static GetLibraryBadge = (list: IListDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (list.ListType === "library") {
                const label: string = DBStringUtils.EscapeHtml((list.LibraryType || "").replace(" Library", ""));
                return '<span style="font-size: 10px; padding: 2px 6px; background: var(--accent-primary); color: white; border-radius: 4px; margin-left: 8px; font-weight: 600;">' + label + '</span>';
            }
            return "";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.GetLibraryBadge");
            return "";
        }
    };

    private static GetCtIndicator = (list: IListDefinition, ctName: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (list.UseContentType) {
                return '<span class="ct-indicator" title="Content Type: ' + ctName + '">CT: ' + ctName + '</span>';
            }
            return "";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.GetCtIndicator");
            return "";
        }
    };

    private static SetupTitleEditing = (card: HTMLDivElement, listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const titleDisplay: Element | null = card.querySelector(".list-name-display");
            if (titleDisplay === null) { return; }
            const HandleDblClick = (e: Event): void => {
                e.stopPropagation();
                DBListManager.StartEditingListName(listId);
            };
            titleDisplay.addEventListener("dblclick", HandleDblClick);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.SetupTitleEditing");
        }
    };

    public static SelectList = (listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBState.SelectedListId = listId;
            DBState.DataModalListId = listId;
            DBListManager.RenderLists();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.SelectList");
        }
    };

    public static ToggleCollapse = (listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }
            list.Collapsed = !list.Collapsed;
            const card: HTMLElement | null = document.getElementById("card_" + listId);
            if (card !== null) {
                card.classList.toggle("collapsed", list.Collapsed);
            }
            DBState.SaveState();
            // Re-flow surrounding cards: an expand/collapse changes this card's height,
            // and the absolute-positioned siblings below it would otherwise overlap.
            // Pass true so AutoArrange honors per-card collapsed state instead of
            // running its layer-count auto-collapse heuristic over the user's choice.
            DBListManager.AutoArrangeLists(true);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.ToggleCollapse");
        }
    };

    public static ToggleContentType = (listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }
            list.UseContentType = !list.UseContentType;
            if (list.UseContentType) {
                if (list.Settings === null) { list.Settings = {} as any; }
                (list.Settings as any).ContentTypesEnabled = true;
                DBModalManager.ShowToast("Content type mode enabled: " + DBStringUtils.GetContentTypeName(list), "success");
            } else {
                DBModalManager.ShowToast("Content type mode disabled", "info");
            }
            DBState.SaveState();
            DBListManager.RenderLists();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.ToggleContentType");
        }
    };

    public static StartEditingListName = (listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }

            const card: HTMLElement | null = document.getElementById("card_" + listId);
            if (card === null) { return; }
            const titleContainer: Element | null = card.querySelector(".list-header-title");
            if (titleContainer === null) { return; }
            const display: Element | null = titleContainer.querySelector(".list-name-display");
            if (display === null) { return; }

            const input: HTMLInputElement = document.createElement("input");
            input.type = "text";
            input.className = "list-name-input";
            input.value = list.Title;

            const HandleBlur = (): void => {
                DBListManager.FinishEditingListName(listId, input.value);
            };
            input.addEventListener("blur", HandleBlur);

            const HandleKeydown = (e: KeyboardEvent): void => {
                if (e.key === "Enter") { e.preventDefault(); input.blur(); }
                else if (e.key === "Escape") {
                    e.preventDefault();
                    input.removeEventListener("blur", HandleBlur);
                    DBListManager.FinishEditingListName(listId, list.Title);
                }
            };
            input.addEventListener("keydown", HandleKeydown);

            display.replaceWith(input);
            input.focus();
            input.select();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.StartEditingListName");
        }
    };

    public static FinishEditingListName = (listId: string, newName: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }

            const card: HTMLElement | null = document.getElementById("card_" + listId);
            if (card === null) { return; }
            const titleContainer: Element | null = card.querySelector(".list-header-title");
            if (titleContainer === null) { return; }
            const input: Element | null = titleContainer.querySelector(".list-name-input");
            if (input === null) { return; }

            const trimmedName: string = newName.trim();
            if (trimmedName) {
                list.Title = trimmedName;
                DBState.SaveState();
                const DrawLater = (): void => { DBCanvas.DrawRelationships(); };
                setTimeout(DrawLater, 50);
            }

            const display: HTMLDivElement = document.createElement("div");
            display.className = "list-name-display";
            display.textContent = list.Title;
            display.title = list.Title;

            const HandleDblClick = (e: Event): void => {
                e.stopPropagation();
                DBListManager.StartEditingListName(listId);
            };
            display.addEventListener("dblclick", HandleDblClick);

            input.replaceWith(display);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.FinishEditingListName");
        }
    };

    private static BuildDependencyGraph = (): { edges: { SourceId: string; TargetId: string }[]; dependents: Map<string, string[]> } => {
        if (ks.kErrorMode === true) { return { edges: [], dependents: new Map() }; }
        try {
            const edges: { SourceId: string; TargetId: string }[] = [];
            const dependents: Map<string, string[]> = new Map();
            const InitDependents = (list: IListDefinition): void => { dependents.set(list.Id, []); };
            DBState.Lists.forEach(InitDependents);

            const ProcessList = (list: IListDefinition): void => {
                const entries: [string, IFieldDefinition][] = Object.entries(list.Properties || {}) as [string, IFieldDefinition][];
                const ProcessField = (entry: [string, IFieldDefinition]): void => {
                    const field: IFieldDefinition = entry[1];
                    if (!field.LookupList) { return; }
                    const FindTarget = (l: IListDefinition): boolean => { return l.Title === field.LookupList; };
                    const target: IListDefinition | undefined = DBState.Lists.find(FindTarget);
                    if (target === undefined) { return; }
                    edges.push({ SourceId: list.Id, TargetId: target.Id });
                    const deps: string[] | undefined = dependents.get(target.Id);
                    if (deps !== undefined) { deps.push(list.Id); }
                };
                entries.forEach(ProcessField);
            };
            DBState.Lists.forEach(ProcessList);
            return { edges: edges, dependents: dependents };
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.BuildDependencyGraph");
            return { edges: [], dependents: new Map() };
        }
    };

    private static ComputeLayers = (edges: { SourceId: string; TargetId: string }[]): Map<string, number> => {
        if (ks.kErrorMode === true) { return new Map(); }
        try {
            const inDegree: Map<string, number> = new Map();
            const forwardEdges: Map<string, string[]> = new Map();
            const InitList = (list: IListDefinition): void => {
                inDegree.set(list.Id, 0);
                forwardEdges.set(list.Id, []);
            };
            DBState.Lists.forEach(InitList);

            const ProcessEdge = (edge: { SourceId: string; TargetId: string }): void => {
                inDegree.set(edge.SourceId, (inDegree.get(edge.SourceId) || 0) + 1);
                const fwd: string[] | undefined = forwardEdges.get(edge.TargetId);
                if (fwd !== undefined) { fwd.push(edge.SourceId); }
            };
            edges.forEach(ProcessEdge);

            const layerMap: Map<string, number> = new Map();
            let queue: string[] = [];
            const FindRoots = (deg: number, id: string): void => { if (deg === 0) { queue.push(id); } };
            inDegree.forEach(FindRoots);

            let currentLayer: number = 0;
            while (queue.length > 0) {
                const nextQueue: string[] = [];
                const ProcessNode = (id: string): void => {
                    layerMap.set(id, currentLayer);
                    const deps: string[] = forwardEdges.get(id) || [];
                    const ReduceDegree = (depId: string): void => {
                        const newDeg: number = (inDegree.get(depId) || 1) - 1;
                        inDegree.set(depId, newDeg);
                        if (newDeg === 0) { nextQueue.push(depId); }
                    };
                    deps.forEach(ReduceDegree);
                };
                queue.forEach(ProcessNode);
                queue = nextQueue;
                currentLayer++;
            }

            const AssignCycleNodes = (list: IListDefinition): void => {
                if (!layerMap.has(list.Id)) { layerMap.set(list.Id, currentLayer); }
            };
            DBState.Lists.forEach(AssignCycleNodes);
            return layerMap;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.ComputeLayers");
            return new Map();
        }
    };

    private static MeasureCardHeights = (): Map<string, number> => {
        if (ks.kErrorMode === true) { return new Map(); }
        try {
            const heights: Map<string, number> = new Map();
            const MeasureOne = (list: IListDefinition): void => {
                const card: HTMLElement | null = document.getElementById("card_" + list.Id);
                heights.set(list.Id, card !== null ? card.offsetHeight : 400);
            };
            DBState.Lists.forEach(MeasureOne);
            return heights;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.MeasureCardHeights");
            return new Map();
        }
    };

    private static TopologicalSort = (layerMap: Map<string, number>, incomingCounts: Map<string, number>): IListDefinition[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const sorted: IListDefinition[] = DBState.Lists.slice();
            const CompareByLayerThenPopularity = (a: IListDefinition, b: IListDefinition): number => {
                const layerA: number = layerMap.get(a.Id) || 0;
                const layerB: number = layerMap.get(b.Id) || 0;
                if (layerA !== layerB) { return layerA - layerB; }
                return (incomingCounts.get(b.Id) || 0) - (incomingCounts.get(a.Id) || 0);
            };
            sorted.sort(CompareByLayerThenPopularity);
            return sorted;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.TopologicalSort");
            return [];
        }
    };

    private static ShouldCollapse = (layerMap: Map<string, number>): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            const panel: HTMLElement | null = document.getElementById("canvasPanel");
            const panelWidth: number = panel !== null ? panel.clientWidth : 1200;
            const expandedSpacing: number = CONFIG.LIST_CARD_WIDTH + CONFIG.ARRANGE_GAP_X;
            const layerSizes: Map<number, number> = new Map();
            const CountPerLayer = (list: IListDefinition): void => {
                const layer: number = layerMap.get(list.Id) || 0;
                layerSizes.set(layer, (layerSizes.get(layer) || 0) + 1);
            };
            DBState.Lists.forEach(CountPerLayer);
            let maxLayerWidth: number = 0;
            const CheckWidth = (count: number): void => {
                const width: number = CONFIG.ARRANGE_OFFSET_X + count * expandedSpacing;
                if (width > maxLayerWidth) { maxLayerWidth = width; }
            };
            layerSizes.forEach(CheckWidth);
            return maxLayerWidth > panelWidth;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.ShouldCollapse");
            return false;
        }
    };

    public static CollapseAllCards = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const CollapseOne = (list: IListDefinition): void => { list.Collapsed = true; };
            DBState.Lists.forEach(CollapseOne);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.CollapseAllCards");
        }
    };

    private static ReorderWithinLayers = (groups: Map<number, IListDefinition[]>, layerNumbers: number[], edges: { SourceId: string; TargetId: string }[]): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const positions: Map<string, number> = new Map();

            const ProcessLayer = (layerNum: number): void => {
                const listsInLayer: IListDefinition[] = groups.get(layerNum) as IListDefinition[];
                if (layerNum === layerNumbers[0]) {
                    const AssignInitial = (list: IListDefinition, index: number): void => { positions.set(list.Id, index); };
                    listsInLayer.forEach(AssignInitial);
                    return;
                }
                const GetGravity = (list: IListDefinition): number => {
                    let sum: number = 0;
                    let count: number = 0;
                    const CheckEdge = (edge: { SourceId: string; TargetId: string }): void => {
                        if (edge.SourceId !== list.Id) { return; }
                        const targetPos: number | undefined = positions.get(edge.TargetId);
                        if (targetPos !== undefined) { sum += targetPos; count++; }
                    };
                    edges.forEach(CheckEdge);
                    return count > 0 ? sum / count : 999;
                };
                const SortByGravity = (a: IListDefinition, b: IListDefinition): number => {
                    return GetGravity(a) - GetGravity(b);
                };
                listsInLayer.sort(SortByGravity);
                const AssignPos = (list: IListDefinition, index: number): void => { positions.set(list.Id, index); };
                listsInLayer.forEach(AssignPos);
            };
            layerNumbers.forEach(ProcessLayer);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.ReorderWithinLayers");
        }
    };

    private static AssignGridPositions = (sortedLists: IListDefinition[], heights: Map<string, number>, layerMap: Map<string, number>, edges: { SourceId: string; TargetId: string }[]): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const spacingX: number = CONFIG.LIST_CARD_WIDTH + CONFIG.ARRANGE_GAP_X;

            const groups: Map<number, IListDefinition[]> = new Map();
            const GroupByLayer = (list: IListDefinition): void => {
                const layer: number = layerMap.get(list.Id) || 0;
                if (!groups.has(layer)) { groups.set(layer, []); }
                (groups.get(layer) as IListDefinition[]).push(list);
            };
            sortedLists.forEach(GroupByLayer);

            const CompareAscending = (a: number, b: number): number => { return a - b; };
            const layerNumbers: number[] = Array.from(groups.keys()).sort(CompareAscending);
            DBListManager.ReorderWithinLayers(groups, layerNumbers, edges);

            let cumulativeY: number = CONFIG.ARRANGE_OFFSET_Y;
            const ProcessLayer = (layerNum: number): void => {
                const listsInLayer: IListDefinition[] = groups.get(layerNum) as IListDefinition[];
                let maxHeight: number = 0;
                const PositionOne = (list: IListDefinition, index: number): void => {
                    list.Position = { X: CONFIG.ARRANGE_OFFSET_X + index * spacingX, Y: cumulativeY };
                    const h: number = heights.get(list.Id) || 400;
                    if (h > maxHeight) { maxHeight = h; }
                };
                listsInLayer.forEach(PositionOne);
                cumulativeY += maxHeight + CONFIG.ARRANGE_GAP_Y;
            };
            layerNumbers.forEach(ProcessLayer);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.AssignGridPositions");
        }
    };

    private static RepositionRenderedCards = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const UpdateOne = (list: IListDefinition): void => {
                const card: HTMLElement | null = document.getElementById("card_" + list.Id);
                if (card === null) { return; }
                card.style.left = list.Position.X + "px";
                card.style.top = list.Position.Y + "px";
            };
            DBState.Lists.forEach(UpdateOne);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.RepositionRenderedCards");
        }
    };

    public static AutoArrangeLists = (preserveCollapseState: boolean = false): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.Lists.length === 0) {
                DBModalManager.ShowToast("No lists to arrange", "error");
                return;
            }

            const graph: { edges: { SourceId: string; TargetId: string }[]; dependents: Map<string, string[]> } = DBListManager.BuildDependencyGraph();
            const layerMap: Map<string, number> = DBListManager.ComputeLayers(graph.edges);

            const incomingCounts: Map<string, number> = new Map();
            const CountIncoming = (deps: string[], id: string): void => { incomingCounts.set(id, deps.length); };
            graph.dependents.forEach(CountIncoming);

            // Skip the layer-based auto-collapse heuristic when the caller is reflowing
            // because the user just toggled one card — otherwise their expand/collapse
            // intent would be silently overridden.
            if (preserveCollapseState !== true && DBListManager.ShouldCollapse(layerMap)) {
                DBListManager.CollapseAllCards();
            }

            const SetTempPosition = (list: IListDefinition): void => { list.Position = { X: 0, Y: 0 }; };
            DBState.Lists.forEach(SetTempPosition);
            DBListManager.RenderLists();

            const FinishLayout = (): void => {
                const heights: Map<string, number> = DBListManager.MeasureCardHeights();
                const sortedLists: IListDefinition[] = DBListManager.TopologicalSort(layerMap, incomingCounts);
                DBListManager.AssignGridPositions(sortedLists, heights, layerMap, graph.edges);
                DBListManager.RepositionRenderedCards();
                DBState.SaveState();
                DBCanvas.UpdateCanvasSize();
                DBCanvas.DrawRelationships();
                if (preserveCollapseState !== true) {
                    const layerCount: number = new Set(layerMap.values()).size;
                    DBModalManager.ShowToast("Arranged " + DBState.Lists.length + " list(s) in " + layerCount + " layer(s)", "success");
                }
            };
            requestAnimationFrame(FinishLayout);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.AutoArrangeLists");
        }
    };

    public static ZoomCanvas = (factor: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const listsContainer: HTMLElement | null = document.getElementById("listsContainer");
            if (listsContainer === null) { return; }
            const panel: HTMLElement | null = listsContainer.parentElement;
            if (panel === null) { return; }

            if (!panel.dataset.zoom) { panel.dataset.zoom = "1"; }
            let currentZoom: number = parseFloat(panel.dataset.zoom);
            currentZoom = currentZoom * factor;
            currentZoom = Math.max(0.5, Math.min(2, currentZoom));
            panel.dataset.zoom = currentZoom.toString();

            listsContainer.style.transform = "scale(" + currentZoom + ")";
            listsContainer.style.transformOrigin = "0 0";

            const svg: HTMLElement | null = document.getElementById("relationshipsCanvas");
            if (svg !== null) { svg.style.transform = "none"; }

            const zoomResetBtn: Element | null = document.querySelector('[data-action="zoom-reset"]');
            if (zoomResetBtn !== null) {
                zoomResetBtn.innerHTML = '<span>\u21BA</span> ' + Math.round(currentZoom * 100) + '%';
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.ZoomCanvas");
        }
    };

    public static ResetZoom = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const listsContainer: HTMLElement | null = document.getElementById("listsContainer");
            if (listsContainer === null) { return; }
            const panel: HTMLElement | null = listsContainer.parentElement;
            if (panel === null) { return; }

            panel.dataset.zoom = "1";
            listsContainer.style.transform = "scale(1)";
            listsContainer.style.transformOrigin = "0 0";

            const svg: HTMLElement | null = document.getElementById("relationshipsCanvas");
            if (svg !== null) { svg.style.transform = "none"; }

            const zoomResetBtn: Element | null = document.querySelector('[data-action="zoom-reset"]');
            if (zoomResetBtn !== null) {
                zoomResetBtn.innerHTML = '<span>\u21BA</span> 100%';
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListManager.ResetZoom");
        }
    };
}
